/**
 * Read/Edit question card + its sortable wrapper for the Quiz Editor.
 *
 * Extracted verbatim from QuizEditorPage.jsx during the Phase 3 refactor
 * (no behaviour change).
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, Trash2, Edit2, ShieldCheck, CheckCircle2, XCircle,
  Loader2, RefreshCw, History, MoreHorizontal,
} from 'lucide-react';
import { useApi } from '../../../hooks/useApi';
import { faviconLoader } from '../../../utils/faviconLoader';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { SIMPLE_TYPES, REVIEW_PILL, effectiveReviewStatus } from './quizMeta';
import { QuestionEditForm } from './QuestionEditForm';
import { QuestionHistoryDialog } from './QuestionHistoryDialog';

export function SortableQuestion({ q, idx, quizId, onChange }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: q.question_id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <QuestionCard
        q={q}
        idx={idx}
        quizId={quizId}
        onChange={onChange}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
}

export function QuestionCard({ q, idx, quizId, onChange, dragHandleProps }) {
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { post, put, del } = useApi();

  const isSimple = SIMPLE_TYPES.has(q.question_type);
  const review = REVIEW_PILL[effectiveReviewStatus(q)] || REVIEW_PILL.approved;
  const ReviewIcon = review.Icon;

  const handleReview = async (action) => {
    setBusy(true);
    try {
      await post(`/quizzes/${quizId}/questions/${q.question_id}/review`, { action });
      toast.success(action === 'approve' ? 'Approved' : 'Rejected');
      // Await the parent refetch so the local card's status-driven render
      // catches up BEFORE we re-enable the buttons. Prevents the "Approve
      // remains active right after click" stale-button bug.
      await onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRegenerate = async () => {
    setBusy(true);
    const releaseFavicon = faviconLoader.start();
    try {
      await post(`/quizzes/${quizId}/questions/${q.question_id}/regenerate`, {});
      toast.success('Question regenerated — review the new wording');
      await onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Regenerate failed');
    } finally {
      releaseFavicon();
      setBusy(false);
    }
  };

  const handleAudit = async () => {
    setBusy(true);
    try {
      const res = await post(`/quizzes/${quizId}/questions/${q.question_id}/audit`, {});
      if (res?.flags?.length) {
        toast.warning(`Audit flagged: ${res.flags.join(', ')}`);
      } else {
        toast.success('Audit passed — no issues found');
      }
      await onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Audit failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await del(`/quizzes/${quizId}/questions/${q.question_id}`);
      toast.success('Question deleted');
      setConfirmDelete(false);
      await onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <QuestionEditForm
        q={q}
        idx={idx}
        onCancel={() => setEditing(false)}
        onSaved={() => { setEditing(false); onChange?.(); }}
        quizId={quizId}
        put={put}
      />
    );
  }

  // Map the (normalised) review status → card border accent + opacity dimming
  // so trainers can eyeball progress without reading every pill.
  const status = effectiveReviewStatus(q);
  const cardCls =
    status === 'rejected'
      ? 'border-l-4 border-l-red-400 opacity-50'
      : status === 'pending'
        ? 'border-l-4 border-l-amber-400'
        : 'border-l-4 border-l-emerald-400';

  return (
    <div
      className={`rounded-lg border bg-card p-4 flex gap-3 transition-opacity ${cardCls}`}
      data-testid={`question-card-${q.question_id}`}
      data-review-status={status}
    >
      <button
        type="button"
        className="text-muted-foreground/60 hover:text-foreground cursor-grab active:cursor-grabbing pt-0.5"
        title="Drag to reorder"
        aria-label="Drag to reorder"
        {...dragHandleProps}
        data-testid={`drag-handle-${q.question_id}`}
      >
        <GripVertical className="w-4 h-4" />
      </button>

      <div className="flex-1 min-w-0 space-y-2.5">
        <div className="flex items-start gap-2 flex-wrap">
          <span className="text-xs font-mono text-muted-foreground">Q{idx}</span>
          <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
            {q.question_type.replace('_', ' ')}
          </Badge>
          <span
            className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${review.cls}`}
            data-testid={`review-pill-${q.question_id}`}
          >
            <ReviewIcon className="w-3 h-3" /> {review.label}
          </span>
          {q.points != null && q.points !== 1 && (
            <Badge variant="secondary" className="text-[10px]">{q.points} pt</Badge>
          )}
        </div>

        <p className="text-sm leading-relaxed">{q.question_text}</p>

        {/* Options for MCQ / TF */}
        {isSimple && q.options?.length > 0 && (
          <ul className="space-y-1 text-sm" data-testid={`options-${q.question_id}`}>
            {q.options.map((o, i) => (
              <li key={`${q.question_id}-opt-${i}`} className={`flex items-center gap-2 px-2 py-1 rounded ${o.is_correct ? 'bg-emerald-50 text-emerald-800' : 'bg-muted/40'}`}>
                {o.is_correct ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" /> : <span className="w-3.5 h-3.5 rounded-full border border-muted-foreground/40 flex-shrink-0" />}
                <span className="break-words">{o.text}</span>
              </li>
            ))}
          </ul>
        )}

        {!isSimple && (
          <p className="text-xs text-amber-600 dark:text-amber-400 italic" data-testid="legacy-type-banner">
            Legacy question type ({q.question_type}). Retired in the May 2026 cleanup — only MCQ and True/False are supported now. This row is read-only and won't run in live sessions.
          </p>
        )}

        {q.explanation && (
          <p className="text-xs text-muted-foreground border-l-2 border-muted pl-2.5">
            <span className="font-medium">Explanation:</span> {q.explanation}
          </p>
        )}

        {/* ── Action row ──────────────────────────────────────────────
         *   Tier 1 (left): Edit + overflow menu (Regenerate / Audit /
         *                  History / Delete) — direct manipulation.
         *   Tier 2 (right): status-driven Approve / Reject — the primary
         *                   trainer task. Hidden when status is already
         *                   settled in that direction. */}
        <div className="flex items-center gap-1 pt-1 flex-wrap">
          {isSimple && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditing(true)}
              disabled={busy}
              data-testid={`edit-btn-${q.question_id}`}
            >
              <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                aria-label="More actions"
                data-testid={`more-btn-${q.question_id}`}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              <DropdownMenuItem
                onClick={handleRegenerate}
                disabled={busy}
                data-testid={`regen-btn-${q.question_id}`}
              >
                <RefreshCw className="w-3.5 h-3.5 mr-2" /> Regenerate AI
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleAudit}
                disabled={busy}
                data-testid={`audit-btn-${q.question_id}`}
              >
                <ShieldCheck className="w-3.5 h-3.5 mr-2" /> Self-audit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setHistoryOpen(true)}
                disabled={busy}
                data-testid={`history-btn-${q.question_id}`}
              >
                <History className="w-3.5 h-3.5 mr-2" /> Version history
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setConfirmDelete(true)}
                disabled={busy}
                data-testid={`delete-btn-${q.question_id}`}
                className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/40"
              >
                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex-1" />

          {status !== 'approved' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleReview('approve')}
              disabled={busy}
              data-testid={`approve-btn-${q.question_id}`}
              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 dark:text-emerald-400 dark:border-emerald-900/60 dark:hover:bg-emerald-950/40"
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
            </Button>
          )}
          {status !== 'rejected' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleReview('reject')}
              disabled={busy}
              data-testid={`reject-btn-${q.question_id}`}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
            >
              <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
            </Button>
          )}
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this question?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes Q{idx} from the quiz. Past session results that referenced
              this question keep their original wording — only future runs are affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={busy} data-testid={`delete-confirm-${q.question_id}`}>
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <QuestionHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        quizId={quizId}
        questionId={q.question_id}
      />
    </div>
  );
}
